/**
 * Import a single-project export (the JSON produced by the share-modal /
 * `/api/projects/:id/export?format=json`, including the legacy `flowpath-
 * export.json`) by reconstructing it on the server.
 *
 * The exported JSON references rows by their old IDs. On import we create a
 * fresh project (new id + new edit/view secrets) and rebuild every timeline,
 * event, branch and merge — translating the old IDs to the new ones so all
 * references stay intact.
 */
import { api, meApi } from './api';
export function isProjectExport(data) {
    if (!data || typeof data !== 'object')
        return false;
    const d = data;
    if (!d.project || typeof d.project !== 'object')
        return false;
    const p = d.project;
    if (typeof p.name !== 'string')
        return false;
    return Array.isArray(d.timelines) && Array.isArray(d.events);
}
export async function importProjectExport(data, token) {
    // 1. Create a fresh project (owned by the user if signed in, anonymous
    //    otherwise — both flows return the new id + secrets).
    const created = token
        ? await meApi.create(token, data.project.name, data.project.description)
        : await api.projects.create(data.project.name, data.project.description);
    const { id: projectId, editSecret, viewSecret } = created;
    // 2. Fresh projects come with a default "Main Timeline" — drop it so we
    //    don't end up with an extra timeline after rebuilding.
    try {
        const fresh = await api.projects.get(projectId, editSecret);
        for (const tl of fresh.timelines) {
            await api.timelines.delete(tl.id, editSecret);
        }
    }
    catch {
        // If the GET fails, carry on — worst case we end up with one extra
        // timeline that the user can delete.
    }
    // 3. Restore project-level settings.
    if (data.project.settings) {
        try {
            await api.projects.update(projectId, editSecret, {
                settings: data.project.settings,
            });
        }
        catch {
            // settings are non-critical
        }
    }
    // 4. Recreate timelines in their original order so `parentId` references
    //    can be resolved as we go.
    const tlMap = new Map();
    const sortedTimelines = [...data.timelines].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    for (const tl of sortedTimelines) {
        const newParent = tl.parentId ? tlMap.get(tl.parentId) : undefined;
        const newTl = await api.timelines.create(projectId, editSecret, {
            name: tl.name,
            color: tl.color,
            parentId: newParent,
        });
        tlMap.set(tl.id, newTl.id);
        if (tl.collapsed) {
            await api.timelines.update(newTl.id, editSecret, { collapsed: true });
        }
    }
    // 5. Recreate events under their (new) timelines.
    const evMap = new Map();
    for (const ev of data.events) {
        const newTlId = tlMap.get(ev.timelineId);
        if (!newTlId)
            continue;
        const newEv = await api.events.create(newTlId, editSecret, {
            title: ev.title,
            subtitle: ev.subtitle,
            dateType: ev.dateType,
            dateValue: ev.dateValue,
            positionX: ev.position?.x ?? 0,
            positionY: ev.position?.y ?? 0,
            tags: ev.tags,
            notes: ev.notes,
        });
        evMap.set(ev.id, newEv.id);
        if (ev.status && ev.status !== 'unchecked') {
            try {
                await api.events.update(newEv.id, editSecret, { status: ev.status });
            }
            catch {
                // non-fatal
            }
        }
    }
    // 6. Recreate branches.
    for (const br of data.branches ?? []) {
        const newSourceEv = evMap.get(br.sourceEventId);
        const newSourceTl = tlMap.get(br.sourceTimelineId);
        const newTargetTl = tlMap.get(br.targetTimelineId);
        if (!newSourceEv || !newSourceTl || !newTargetTl)
            continue;
        try {
            await api.branches.create(projectId, editSecret, {
                sourceEventId: newSourceEv,
                sourceTimelineId: newSourceTl,
                targetTimelineId: newTargetTl,
                condition: br.condition,
                label: br.label,
            });
        }
        catch {
            // skip malformed branches
        }
    }
    // 7. Recreate merges.
    for (const m of data.merges ?? []) {
        const newSources = (m.sourceTimelineIds ?? [])
            .map((id) => tlMap.get(id))
            .filter((id) => !!id);
        const newTarget = tlMap.get(m.targetTimelineId);
        if (!newTarget || newSources.length === 0)
            continue;
        try {
            await api.merges.create(projectId, editSecret, {
                sourceTimelineIds: newSources,
                targetTimelineId: newTarget,
                mergeEventId: m.mergeEventId ? evMap.get(m.mergeEventId) : undefined,
                condition: m.condition,
                strategy: m.strategy,
            });
        }
        catch {
            // skip
        }
    }
    return { id: projectId, editSecret, viewSecret, name: data.project.name };
}
